import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloCache, useApolloClient } from '@apollo/client';

import {
  RuntimeSettingsListQueryVariables,
  useClearRuntimeSettingValueMutation,
  useRuntimeSettingHistoryQuery,
  useRuntimeSettingSectionsListQuery,
  useRuntimeSettingsListQuery,
  useSetRuntimeSettingValueMutation,
} from 'core/graphql';

import {
  runtimeCrashResult,
  IRuntimeActionResult,
  IRuntimeChange,
  IRuntimeSetting,
  pickRuntimeLabel,
  RuntimeScalar,
  runtimeLabelOverrideKey,
  toRuntimeActionResult,
  toRuntimeChange,
  toRuntimeSetting,
} from './runtimeSettingsModel';

/**
 * ТЗ 0093 этап 2б — runtime-настройки: список ключей, запись и сброс.
 *
 * Ответ мутации несёт ключ целиком — Apollo пишет его в кэш по `key`
 * (`RuntimeSettingType.keyFields`), и строка обновляется во всех списках
 * (весь реестр в «Роботах», раздел `ai` в «ИИ») без перечитывания и без
 * `refetchQueries` по имени.
 *
 * Производные от ключей списки — расписания задач (действует ли запись) и
 * журнал изменений — после настоящего изменения выбрасываются из кэша:
 * открытый список сразу перечитает себя сам, закрытый — при открытии, без
 * показа устаревшего.
 *
 * Все запросы раздела — `cache-and-network` ТОЛЬКО на первый показ, дальше
 * `cache-first` (`RUNTIME_FETCH`). Мутация в Apollo 3.3 при старте зовёт
 * `broadcastQueries()`, и активный `cache-and-network` запрос без
 * `nextFetchPolicy` уходит в сеть в ту же миллисекунду, что и мутация: сервер
 * читает список ДО коммита, ответ приходит ПОСЛЕ ответа мутации и затирает
 * ключ старым значением — «Сохранено», а в поле снова прежнее число.
 * Перечитать явно («Повторить») по-прежнему можно: `refetch` идёт в сеть.
 */

export const RUNTIME_FETCH = {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-first',
} as const;

const ALL_VARIABLES: RuntimeSettingsListQueryVariables = {};

export const evictRuntimeDerived = (cache: ApolloCache<unknown>): void => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'scheduledTasks' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'runtimeSettingChanges' });
};

export interface IUseRuntimeSettings {
  settings: IRuntimeSetting[];
  loading: boolean;
  error: boolean;
  refetch: () => void;
  setValue: (
    key: string,
    value: RuntimeScalar,
    reason?: string,
  ) => Promise<IRuntimeActionResult>;
  clearValue: (key: string, reason?: string) => Promise<IRuntimeActionResult>;
}

/** Ключи реестра, видимые роли; `section` — только один раздел сервера. */
export const useRuntimeSettings = (section?: string): IUseRuntimeSettings => {
  const { t, i18n } = useTranslation();
  const client = useApolloClient();
  const variables = useMemo<RuntimeSettingsListQueryVariables>(
    () => (section ? { section } : ALL_VARIABLES),
    [section],
  );
  const query = useRuntimeSettingsListQuery({ variables, ...RUNTIME_FETCH });
  const [setMut] = useSetRuntimeSettingValueMutation();
  const [clearMut] = useClearRuntimeSettingValueMutation();

  const settings = useMemo(
    () =>
      (query.data?.runtimeSettings ?? []).map((raw) => {
        const setting = toRuntimeSetting(raw, i18n.language);
        const overrideKey = runtimeLabelOverrideKey(setting.key);
        return overrideKey ? { ...setting, label: t(overrideKey) } : setting;
      }),
    [query.data, i18n.language, t],
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(() => undefined);
  }, [refetchQuery]);

  const setValue = useCallback(
    async (
      key: string,
      value: RuntimeScalar,
      reason?: string,
    ): Promise<IRuntimeActionResult> => {
      try {
        const { data } = await setMut({
          variables: {
            key,
            value: JSON.stringify(value),
            reason: reason?.trim() || null,
          },
        });
        const result = toRuntimeActionResult(data?.setRuntimeSetting);
        if (result.changed) evictRuntimeDerived(client.cache);
        return result;
      } catch (caught) {
        return runtimeCrashResult(caught);
      }
    },
    [client, setMut],
  );

  const clearValue = useCallback(
    async (key: string, reason?: string): Promise<IRuntimeActionResult> => {
      try {
        const { data } = await clearMut({
          variables: { key, reason: reason?.trim() || null },
        });
        const result = toRuntimeActionResult(data?.clearRuntimeSetting);
        if (result.changed) evictRuntimeDerived(client.cache);
        return result;
      } catch (caught) {
        return runtimeCrashResult(caught);
      }
    },
    [client, clearMut],
  );

  return {
    settings,
    loading: query.loading && !query.data,
    error: Boolean(query.error) && !query.data,
    refetch,
    setValue,
    clearValue,
  };
};

export interface IRuntimeSection {
  id: string;
  label: string;
}

/** Разделы сервера (в его порядке), в которых роли виден хоть один ключ. */
export const useRuntimeSettingSections = () => {
  const { i18n } = useTranslation();
  const query = useRuntimeSettingSectionsListQuery({ ...RUNTIME_FETCH });
  const sections = useMemo<IRuntimeSection[]>(
    () =>
      (query.data?.runtimeSettingSections ?? []).map((section) => ({
        id: section.id,
        label: pickRuntimeLabel(
          i18n.language,
          section.labelRu,
          section.labelPl,
        ),
      })),
    [query.data, i18n.language],
  );
  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(() => undefined);
  }, [refetchQuery]);
  return {
    sections,
    loaded: Boolean(query.data),
    loading: query.loading && !query.data,
    error: Boolean(query.error) && !query.data,
    refetch,
  };
};

/** Сколько последних изменений показывает журнал. */
export const RUNTIME_HISTORY_LIMIT = 50;

/** Журнал изменений видимых ключей (и выключенных строк планировщика). */
export const useRuntimeSettingHistory = (limit = RUNTIME_HISTORY_LIMIT) => {
  const query = useRuntimeSettingHistoryQuery({
    variables: { limit },
    ...RUNTIME_FETCH,
  });
  const changes = useMemo<IRuntimeChange[]>(
    () => (query.data?.runtimeSettingChanges ?? []).map(toRuntimeChange),
    [query.data],
  );
  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(() => undefined);
  }, [refetchQuery]);
  return {
    changes,
    loading: query.loading && !query.data,
    error: Boolean(query.error) && !query.data,
    refetch,
  };
};
